import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { FormContextValues, FormErrors, RequiredFields, FieldValidationFunction, FormProviderProps, FieldValidators, FormState, FieldValue } from './Form.types';
import { mergeRefs } from '../../utils/utils';
import { useDebounce } from '../../utils/useDebounce';
import { isEqualFormState } from './Form.utils';

const FormContext = createContext<FormContextValues<FormState> | undefined>(undefined);

export const FormProvider = <T extends FormState>(props: FormProviderProps<T>) => {
  const { 
    formRef,
    formState: defaultFormState = {} as T, 
    onFormStateChange, 
    formValidator, 
    onSubmit, 
    validateOnChange,
    autosave = false,
    autosaveDelayMs,
  } = props;

  // Create a ref to the form element
  const internalFormRef = useRef<HTMLFormElement>(null);

  // State for form values, errors, required fields, and validation functions
  const [initialFormState, setInitialFormState] = useState<T>(defaultFormState);
  const [formState, setFormState] = useState<T>(defaultFormState);
  const [formErrors, setFormErrors] = useState<FormErrors<T>>({});
  const [requiredFields, setRequiredFields] = useState<RequiredFields<T>>({});
  const [fieldValidators, setFieldValidators] = useState<FieldValidators<T>>({});
  const [isPristine, setIsPristine] = useState(true);

  useEffect(() => {
    if (!isEqualFormState(initialFormState, defaultFormState)) {
      setFormState(defaultFormState);
      setInitialFormState(defaultFormState);
      setIsPristine(true);
    }
  }, [defaultFormState]);

  // Memoized function to update form error for a field using name and error
  const updateFieldError = useCallback((name: keyof T, error: string) => {
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  }, [formErrors]);

  // Memoized function to set error for a specific field
  const setFieldError = useCallback((name: keyof T) => {
    return (message: string) => {
      updateFieldError(name, message);
    };
  }, [updateFieldError]);

  // Update validation functions for a specific field
  const updateFieldValidators = useCallback((name: keyof T, validate?: FieldValidationFunction) => {
    setFieldValidators((prevValidationFunctions) => ({ ...prevValidationFunctions, [name]: validate }));
  }, []);

  // Update required fields
  const updateRequiredFields = useCallback((name: keyof T, required?: boolean) => {
    setRequiredFields((prevRequiredFields) => ({ ...prevRequiredFields, [name]: !!required }));
  }, []);

  // Update the value of a form field
  const updateFieldValue = useCallback((name: string, value: FieldValue) => {
    // Clear the error for the field
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));

    // Update the form values
    const updatedState: T = {
      ...formState,
      [name]: value,
    };
    setFormState(updatedState);
    setIsPristine(false);
    onFormStateChange?.(updatedState, updateFieldError);

    // Validate the field if validateOnChange is true
    if (validateOnChange) {
      fieldValidators[name]?.(setFieldError(name), value);
    }
  }, [formState, validateOnChange, fieldValidators, onFormStateChange]);

  // Validate required fields
  const validateRequiredFields = () => {
    let valid = true;
    Object.entries(requiredFields).forEach(([fieldName, required]) => {
      if (!required) return true;
      if (!formState[fieldName] || (Array.isArray(formState[fieldName]) && formState[fieldName].length === 0)) {
        setFieldError(fieldName)(`${fieldName} is required`);
        valid = false
      }
    });
    return valid;
  };

  // Validate each field with a validation function
  const validateEachField = () => {
    const fieldsWithValidateFunction = Object.keys(fieldValidators);
    return fieldsWithValidateFunction.length
      ? fieldsWithValidateFunction.every((fieldName) => {
        const isFieldValidated = fieldValidators[fieldName]?.(setFieldError(fieldName), formState[fieldName]);
        return isFieldValidated === undefined
          ? true // considering field validated if there is no field validator for it
          : isFieldValidated;
      })
      : true;
  };

  // Handle form submission
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    // Validate required fields, individual fields, and the entire form
    const areRequiredFieldsValid = validateRequiredFields();
    const areFieldsValid = validateEachField();
    const isFormValid = formValidator ? formValidator(formState, updateFieldError) : true;

    // If any validation fails, return early
    if (!areRequiredFieldsValid || !areFieldsValid || !isFormValid) {
      return;
    }

    // Call the onSubmit callback with the form values
    onSubmit?.(formState, updateFieldError);
  };

  const handleReset = useCallback(() => {
    // Reset the form state to the initial values
    setFormState(defaultFormState);
    setFormErrors({});
    setIsPristine(true);
  }, [defaultFormState]);

  // Autosave functionality
  const performAutosave = useCallback(() => {
    if (!autosave || !onSubmit) return;

    // Validate required fields, individual fields, and the entire form
    const areRequiredFieldsValid = validateRequiredFields();
    const areFieldsValid = validateEachField();
    const isFormValid = formValidator ? formValidator(formState, updateFieldError) : true;

    // If all validations pass, submit the form
    if (areRequiredFieldsValid && areFieldsValid && isFormValid) {
      onSubmit(formState, updateFieldError);
      setIsPristine(true);
    }
  }, [autosave, onSubmit, formState, formValidator, updateFieldError]);

  // Create debounced version of autosave if autosaveDelayMs is provided
  const { debouncedCallback: debouncedAutosave } = useDebounce(performAutosave, autosaveDelayMs || 0);

  // Trigger autosave when form state changes
  useEffect(() => {
    if (autosave && !isPristine) {
      if (autosaveDelayMs) {
        debouncedAutosave();
      } else {
        performAutosave();
      }
    }
  }, [formState, isPristine]);

  // Create the context value
  const contextValue: FormContextValues = {
    formRef: mergeRefs(formRef, internalFormRef),
    formState,
    updateFieldValue,
    formErrors,
    updateFieldError,
    onFormSubmit: handleSubmit,
    updateFieldValidators,
    updateRequiredFields,
    resetForm: handleReset,
  };

  // Provide the form context to the child components
  return (
    <FormContext.Provider value={contextValue}>
      {props.children}
    </FormContext.Provider>
  );
};

export default FormContext;